import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, Moon, Sun, Monitor, CheckCircle, AlertCircle } from 'lucide-react'
import { useUserProfile } from '../../hooks/useBilling'
import { useOnboarding } from '../../hooks/useOnboarding'
import { api } from '../../lib/api'
import { setThemeClass } from '../../lib/utils'
import TwoFactorSetup from '../../components/TwoFactorSetup'
import { OnboardingTooltip } from '../../components/onboarding/OnboardingTooltip'

export default function AccountSettings() {
  const navigate = useNavigate()
  const { profile, loading: profileLoading, refetch } = useUserProfile()
  const { progress, completeStep } = useOnboarding()
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  
  // Check if we're on the profile onboarding step
  const isOnboardingProfileStep = progress?.current_step === 'profile' && 
    !progress?.steps_completed?.includes('profile')
  const [theme, setTheme] = useState(localStorage.getItem('theme') || 'auto')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)
  
  // 2FA state
  const [totpEnabled, setTotpEnabled] = useState(false)
  const [totpLoading, setTotpLoading] = useState(true)
  const [show2FAModal, setShow2FAModal] = useState(false)

  useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || '')
      setLastName(profile.last_name || '')
    }
  }, [profile])

  useEffect(() => {
    const fetchTotpStatus = async () => {
      try {
        const { data } = await api.get('/fapi/totp/status')
        setTotpEnabled(data.enabled)
      } catch (err) {
        console.error('Failed to fetch TOTP status:', err)
      } finally {
        setTotpLoading(false)
      }
    }
    fetchTotpStatus()
  }, [])

  useEffect(() => {
    const fetchTheme = async () => {
      try {
        const { data } = await api.get('/settings/me')
        if (data.theme) {
          setTheme(data.theme)
          localStorage.setItem('theme', data.theme)
          setThemeClass()
        }
      } catch {}
    }
    fetchTheme()
  }, [])

  const handleSaveProfile = async () => {
    setSaving(true)
    setMessage(null)
    try {
      await api.patch('/fapi/users/me', { first_name: firstName, last_name: lastName })
      setMessage({ type: 'success', text: 'Profil mis à jour avec succès.' })
      refetch()
      
      // If on onboarding profile step, complete it and navigate to agents page
      if (isOnboardingProfileStep && firstName && lastName) {
        await completeStep('profile')
        window.dispatchEvent(new CustomEvent('onboarding:step-completed'))
        window.dispatchEvent(new CustomEvent('onboarding:widget-expand'))
        // Navigate to agents page for next step
        navigate('/agents')
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Erreur lors de la mise à jour.' })
    } finally {
      setSaving(false)
    }
  }

  const handleSaveTheme = async () => {
    setSaving(true)
    setMessage(null)
    try {
      await api.put('/settings/me', { theme })
      localStorage.setItem('theme', theme)
      setThemeClass()
      setMessage({ type: 'success', text: 'Préférence de thème enregistrée.' })
    } catch (err: any) {
      setMessage({ type: 'error', text: err?.response?.data?.detail || 'Erreur lors de l\'enregistrement.' })
    } finally {
      setSaving(false)
    }
  }

  const handle2FASuccess = () => {
    setShow2FAModal(false)
    setTotpEnabled(!totpEnabled)
  }

  if (profileLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Onboarding Tooltip for Profile Step */}
      {isOnboardingProfileStep && (
        <OnboardingTooltip
          targetSelector='[data-onboarding="profile-save-btn"]'
          title="Complétez votre profil"
          description="Renseignez votre prénom et nom, puis cliquez sur Enregistrer pour continuer."
          step={1}
          totalSteps={1}
          position="left"
          isVisible={true}
          showNextButton={false}
        />
      )}

      {/* Message */}
      {message && (
        <div className={`flex items-center gap-2 rounded-xl p-4 text-sm ${
          message.type === 'success' 
            ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-200 dark:border-emerald-900/60'
            : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-950/40 dark:text-red-200 dark:border-red-900/60'
        }`}>
          {message.type === 'success' ? <CheckCircle className="h-4 w-4" /> : <AlertCircle className="h-4 w-4" />}
          {message.text}
        </div>
      )}

      {/* Personal Info */}
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 dark:border-zinc-700 dark:bg-zinc-800/50">
        <h3 className="mb-4 font-semibold text-zinc-900 dark:text-white">Informations personnelles</h3>
        
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Prénom
              </label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-zinc-900 transition focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
                placeholder="Votre prénom"
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                Nom
              </label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                className="w-full rounded-xl border border-zinc-300 bg-white px-4 py-2.5 text-zinc-900 transition focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 dark:border-zinc-600 dark:bg-zinc-900 dark:text-white"
                placeholder="Votre nom"
              />
            </div>
          </div>
          
          <div>
            <label className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Email
            </label>
            <div className="flex items-center gap-2">
              <input
                type="email"
                value={profile?.email || ''}
                disabled
                className="w-full rounded-xl border border-zinc-200 bg-zinc-100 px-4 py-2.5 text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400"
              />
              {profile?.is_verified && (
                <span className="flex items-center gap-1 whitespace-nowrap text-xs text-emerald-600 dark:text-emerald-400">
                  <CheckCircle className="h-3 w-3" /> Vérifié
                </span>
              )}
            </div>
          </div>
          
          <button
            onClick={handleSaveProfile}
            disabled={saving}
            className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600 disabled:opacity-50"
            data-onboarding="profile-save-btn"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>

      {/* Theme Preferences */}
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 dark:border-zinc-700 dark:bg-zinc-800/50">
        <h3 className="mb-4 font-semibold text-zinc-900 dark:text-white">Préférences d'affichage</h3>
        
        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Thème
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { value: 'auto', label: 'Auto', icon: Monitor },
                { value: 'light', label: 'Clair', icon: Sun },
                { value: 'dark', label: 'Sombre', icon: Moon },
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => setTheme(value)}
                  className={`flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition ${
                    theme === value
                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300'
                      : 'border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-zinc-600'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>
          
          <button
            onClick={handleSaveTheme}
            disabled={saving}
            className="rounded-full bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-emerald-500/30 transition hover:bg-emerald-600 disabled:opacity-50"
          >
            {saving ? 'Enregistrement...' : 'Enregistrer le thème'}
          </button>
        </div>
      </div>

      {/* Security */}
      <div className="rounded-2xl border border-zinc-200 bg-zinc-50 p-6 dark:border-zinc-700 dark:bg-zinc-800/50">
        <div className="mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-emerald-500" />
          <h3 className="font-semibold text-zinc-900 dark:text-white">Sécurité</h3>
        </div>
        
        <div className="space-y-4">
          {/* 2FA */}
          <div className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <div>
              <h4 className="font-medium text-zinc-900 dark:text-white">
                Authentification à deux facteurs (2FA)
              </h4>
              <p className="mt-1 text-sm text-zinc-500">
                {totpEnabled 
                  ? 'Votre compte est protégé par la 2FA.'
                  : 'Ajoutez une couche de sécurité supplémentaire.'}
              </p>
            </div>
            {totpLoading ? (
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
            ) : (
              <div className="flex items-center gap-3">
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                  totpEnabled 
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                    : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                }`}>
                  {totpEnabled ? 'Activé' : 'Désactivé'}
                </span>
                <button
                  onClick={() => setShow2FAModal(true)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition ${
                    totpEnabled
                      ? 'border border-red-300 text-red-600 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950/40'
                      : 'bg-emerald-500 text-white hover:bg-emerald-600'
                  }`}
                >
                  {totpEnabled ? 'Désactiver' : 'Activer'}
                </button>
              </div>
            )}
          </div>

          {/* Password Change */}
          <div className="flex items-center justify-between rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
            <div>
              <h4 className="font-medium text-zinc-900 dark:text-white">Mot de passe</h4>
              <p className="mt-1 text-sm text-zinc-500">
                Modifiez votre mot de passe régulièrement pour plus de sécurité.
              </p>
            </div>
            <button
              onClick={() => window.location.href = '/forgot-password'}
              className="rounded-full border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-700 transition hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
            >
              Changer
            </button>
          </div>
        </div>
      </div>

      {/* Danger Zone */}
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6 dark:border-red-900/60 dark:bg-red-950/40">
        <h3 className="mb-2 font-semibold text-red-700 dark:text-red-300">Zone danger</h3>
        <p className="mb-4 text-sm text-red-600 dark:text-red-400">
          La suppression de votre compte est irréversible. Toutes vos données seront perdues.
        </p>
        <button
          onClick={() => alert('Pour supprimer votre compte, veuillez contacter le support.')}
          className="rounded-full border border-red-300 bg-white px-5 py-2.5 text-sm font-semibold text-red-600 transition hover:bg-red-50 dark:border-red-800 dark:bg-transparent dark:text-red-400 dark:hover:bg-red-950/40"
        >
          Supprimer mon compte
        </button>
      </div>

      {/* 2FA Modal */}
      <TwoFactorSetup
        open={show2FAModal}
        onClose={() => setShow2FAModal(false)}
        onSuccess={handle2FASuccess}
        isEnabled={totpEnabled}
      />
    </div>
  )
}
